// Highlighting a *thing* rather than the square under it: an inverted hull.
//
// A ring on the floor says which square. It stopped being enough the moment a
// square could hold a lane, a heap and a piece at once — and at a 3/4 pitch a
// tall piece covers most of the tile behind it, so the marker for the piece is
// the thing the piece is standing on top of. A rim of colour round the model
// answers "which entity" without competing for the floor.
//
// The technique: draw the model a second time, **back faces only**, with every
// vertex pushed out along its normal. The front of the shell is discarded, so
// what survives is the part of it the original does not cover — an even rim,
// with no post-process, no second render target and no edge detection.
//
// Two things it needs to be right, and both were arrived at the hard way
// elsewhere often enough to be worth stating:
//
// - **Smoothed normals, not the model's own** (`smoothNormals`). A hard edge
//   is authored as coincident vertices carrying *different* normals, so
//   expanding along them pulls the shell apart at every crease and the rim
//   comes to bits exactly where the silhouette is sharpest. Averaging by
//   position welds them.
// - **Expansion in screen space, not world space.** A world-space offset is a
//   rim that thickens as you zoom in and vanishes as you zoom out, which on a
//   board that grows for a whole run means a highlight that stops working.
//   Offsetting in clip space by a pixel count is the same width at every zoom
//   and under both projections.

import {
  BackSide,
  BufferAttribute,
  BufferGeometry,
  Color,
  ShaderMaterial,
  Vector2
} from "three";

/**
 * How wide the rim is, in CSS pixels.
 *
 * Pixels rather than world units, and CSS rather than device: the offset is
 * applied in clip space against a resolution measured the same way, so the rim
 * is the same width on a retina screen as on a cheap one.
 *
 * Three is about as thin as reads at this pitch against a lit model; much more
 * and the rim starts eating the silhouette it is meant to be describing.
 */
export const OUTLINE_PIXELS = 3;

/** How much of the rim is left while the army is drawn back for a belt tool. */
const FADED_OPACITY = 0.35;

/**
 * Welding tolerance, as a reciprocal: positions are rounded to this many parts
 * of a board square. The set is authored at one square to the piece, so 1e-4
 * is far below anything an artist placed on purpose and far above float noise.
 */
const WELD = 1e4;

/**
 * Per-vertex normals averaged over every vertex sharing a *position*.
 *
 * The one interesting function here, and the weighting is the decision in it:
 * each face counts for the **angle it turns through at that vertex**, not for
 * its area. Area weighting is the usual choice and it is wrong for a hull,
 * because it makes the answer depend on how somebody triangulated a flat
 * quad — measured on a plain cube, whose corners came out at 1/√6 along one
 * axis and 0.816 along another instead of the diagonal, tilting the rim by
 * nothing the artist did. Angle weighting cannot see the triangulation.
 *
 * `fallback` is the geometry's own normals, used where the sum cancels to
 * nothing (a zero-area face, or a sheet with a face on each side). Pure and
 * exported because the whole failure mode of the shell is invisible until it
 * is on screen: a cracked rim looks like a rendering bug, not like arithmetic.
 */
/** The angle a triangle turns through at `v`, its other two corners `p` and `q`. */
function cornerAngle(
  position: ArrayLike<number>,
  v: number,
  p: number,
  q: number
): number {
  const ux = position[p * 3] - position[v * 3];
  const uy = position[p * 3 + 1] - position[v * 3 + 1];
  const uz = position[p * 3 + 2] - position[v * 3 + 2];
  const vx = position[q * 3] - position[v * 3];
  const vy = position[q * 3 + 1] - position[v * 3 + 1];
  const vz = position[q * 3 + 2] - position[v * 3 + 2];

  const lengths = Math.hypot(ux, uy, uz) * Math.hypot(vx, vy, vz);
  if (lengths < 1e-12) return 0;
  const cosine = (ux * vx + uy * vy + uz * vz) / lengths;
  return Math.acos(Math.min(1, Math.max(-1, cosine)));
}

export function smoothNormals(
  position: ArrayLike<number>,
  index?: ArrayLike<number> | null,
  fallback?: ArrayLike<number>
): Float32Array {
  const count = Math.floor(position.length / 3);
  const out = new Float32Array(count * 3);
  if (count === 0) return out;

  // Which welded point each vertex belongs to.
  const slot = new Int32Array(count);
  const points = new Map<string, number>();
  let slots = 0;
  for (let v = 0; v < count; v++) {
    const key =
      Math.round(position[v * 3] * WELD) +
      "|" +
      Math.round(position[v * 3 + 1] * WELD) +
      "|" +
      Math.round(position[v * 3 + 2] * WELD);
    let point = points.get(key);
    if (point === undefined) {
      point = slots++;
      points.set(key, point);
    }
    slot[v] = point;
  }

  const sums = new Float64Array(slots * 3);
  const faces = index ? index.length : count;
  for (let i = 0; i + 2 < faces; i += 3) {
    const a = index ? index[i] : i;
    const b = index ? index[i + 1] : i + 1;
    const c = index ? index[i + 2] : i + 2;

    const ax = position[a * 3];
    const ay = position[a * 3 + 1];
    const az = position[a * 3 + 2];
    const ux = position[b * 3] - ax;
    const uy = position[b * 3 + 1] - ay;
    const uz = position[b * 3 + 2] - az;
    const vx = position[c * 3] - ax;
    const vy = position[c * 3 + 1] - ay;
    const vz = position[c * 3 + 2] - az;

    let nx = uy * vz - uz * vy;
    let ny = uz * vx - ux * vz;
    let nz = ux * vy - uy * vx;
    const area = Math.hypot(nx, ny, nz);
    if (area < 1e-12) continue;
    nx /= area;
    ny /= area;
    nz /= area;

    const corners: [number, number, number][] = [
      [a, b, c],
      [b, c, a],
      [c, a, b]
    ];
    for (const [v, p, q] of corners) {
      const s = slot[v] * 3;
      const weight = cornerAngle(position, v, p, q);
      sums[s] += nx * weight;
      sums[s + 1] += ny * weight;
      sums[s + 2] += nz * weight;
    }
  }

  for (let v = 0; v < count; v++) {
    const s = slot[v] * 3;
    let x = sums[s];
    let y = sums[s + 1];
    let z = sums[s + 2];
    let length = Math.hypot(x, y, z);
    if (length < 1e-12 && fallback) {
      x = fallback[v * 3];
      y = fallback[v * 3 + 1];
      z = fallback[v * 3 + 2];
      length = Math.hypot(x, y, z);
    }
    if (length < 1e-12) {
      out[v * 3 + 1] = 1;
      continue;
    }
    out[v * 3] = x / length;
    out[v * 3 + 1] = y / length;
    out[v * 3 + 2] = z / length;
  }

  return out;
}

/**
 * The shell's vertex program: a real expansion in **view space**, sized so it
 * lands on a pixel count.
 *
 * Offsetting in clip space instead is a line shorter and was measured worse.
 * It leaves the vertex's depth exactly where it was, so at every concave
 * crease the expanded back face is *nearer* than the surface in front of it
 * and the rim pops through the middle of the model — on the pawn, a red ring
 * round its own collar. Moving the vertex in three dimensions moves its depth
 * with it, and a back face pushed along its own normal goes away from the
 * camera, so the depth test throws out exactly the fragments that were
 * showing.
 *
 * The scale is exact under both projections and is read off the projection
 * matrix rather than passed in: `[1][1]` is the vertical scale, and `[2][3]`
 * is 0 for an orthographic camera and -1 for a perspective one — which is the
 * whole of the difference between them here, since only the second divides by
 * distance. So the rim is `thickness` pixels wide at the silhouette, where the
 * normal lies in the screen plane, at any zoom and either projection.
 */
const HULL_VERTEX = /* glsl */ `
uniform vec2 resolution;
uniform float thickness;

void main() {
  vec4 view = modelViewMatrix * vec4(position, 1.0);
  vec3 out_ = normalize(normalMatrix * normal);

  float distance = projectionMatrix[2][3] == 0.0 ? 1.0 : -view.z;
  float pixelsPerUnit = projectionMatrix[1][1] * resolution.y * 0.5 / max(distance, 1e-4);
  view.xyz += out_ * (thickness / max(pixelsPerUnit, 1e-6));

  gl_Position = projectionMatrix * view;
}
`;

/**
 * Flat colour, and the one include that matters: the uniform is in the
 * renderer's working space, so without `colorspace_fragment` the rim comes out
 * a different colour from the palette entry it was asked for.
 */
const HULL_FRAGMENT = /* glsl */ `
uniform vec3 outlineColor;
uniform float outlineOpacity;

void main() {
  gl_FragColor = vec4(outlineColor, outlineOpacity);
  #include <colorspace_fragment>
}
`;

/**
 * The shells' shared workshop: hull geometries, and one material per colour.
 *
 * One instance for the whole board, because both halves of "an entity" want
 * the same rim — `PieceViews` for the army, `IndustryViews` for the buildings
 * — and a second copy of the resolution uniform is a second thing to remember
 * on resize. Defaults to its own where a caller has none, which is what keeps
 * the view tests constructing views with nothing but geometry.
 */
export class Outlines {
  /** Hull geometry per source geometry. Built once; the sources are shared. */
  private hulls = new Map<BufferGeometry, BufferGeometry>();
  private materials = new Map<number, ShaderMaterial>();
  /** Held by every material's uniform, so a resize is one write. */
  private resolution = { value: new Vector2(1, 1) };
  private thickness = { value: OUTLINE_PIXELS };
  private opacity = { value: 1 };
  private faded = false;

  /**
   * The expanded twin of a geometry: position and index as authored, normals
   * welded.
   *
   * A separate geometry rather than an extra attribute on the source, because
   * the sources are loaded once, shared by every mesh of that rank and
   * deliberately never disposed — writing to one from here would be a mesh
   * nobody asked to change.
   */
  hullFor(geometry: BufferGeometry): BufferGeometry {
    const existing = this.hulls.get(geometry);
    if (existing) return existing;

    const position = geometry.getAttribute("position");
    const normal = geometry.getAttribute("normal");
    const index = geometry.getIndex();

    const hull = new BufferGeometry();
    hull.setAttribute("position", position.clone());
    hull.setAttribute(
      "normal",
      new BufferAttribute(
        smoothNormals(
          position.array as ArrayLike<number>,
          index?.array as ArrayLike<number> | undefined,
          normal?.array as ArrayLike<number> | undefined
        ),
        3
      )
    );
    if (index) hull.setIndex(index.clone());

    this.hulls.set(geometry, hull);
    return hull;
  }

  /** The shell material for one highlight colour, minted on first use. */
  materialFor(color: number): ShaderMaterial {
    const existing = this.materials.get(color);
    if (existing) return existing;

    const material = new ShaderMaterial({
      vertexShader: HULL_VERTEX,
      fragmentShader: HULL_FRAGMENT,
      uniforms: {
        resolution: this.resolution,
        thickness: this.thickness,
        outlineOpacity: this.opacity,
        outlineColor: { value: new Color(color) }
      },
      // Back faces only — that *is* the technique. And no depth write: the
      // shell is a mark on top of the board, not another solid competing with
      // the model it belongs to.
      side: BackSide,
      depthWrite: false,
      transparent: this.faded
    });
    this.materials.set(color, material);
    return material;
  }

  /** Told the viewport in CSS pixels, so the rim keeps its width. */
  setSize(width: number, height: number): void {
    this.resolution.value.set(Math.max(1, width), Math.max(1, height));
  }

  /** Drawn back with the army it is round, or the rim asserts what the fade denies. */
  setFaded(faded: boolean): void {
    if (this.faded === faded) return;
    this.faded = faded;
    this.opacity.value = faded ? FADED_OPACITY : 1;
    for (const material of this.materials.values()) {
      material.transparent = faded;
      material.needsUpdate = true;
    }
  }

  dispose(): void {
    for (const hull of this.hulls.values()) hull.dispose();
    for (const material of this.materials.values()) material.dispose();
    this.hulls.clear();
    this.materials.clear();
  }
}
